import LegalPageLayout, { Section, Paragraph, List } from '../../components/LegalPageLayout'

export default function AccessibilityStatementPage() {
  return (
    <LegalPageLayout title="Accessibility Statement" lastUpdated="February 1, 2026">
      {/* Commitment */}
      <Section title="Our Commitment to Accessibility">
        <Paragraph>
          DIZEE is committed to ensuring digital accessibility for people with disabilities. We are continually improving
          the user experience for everyone and applying the relevant accessibility standards to ensure we provide equal
          access to all of our users.
        </Paragraph>
        <Paragraph>
          We believe that everyone should be able to use our platform with ease, regardless of their abilities or the
          technology they use to access it.
        </Paragraph>
      </Section>

      {/* Conformance Status */}
      <Section title="Conformance Status">
        <div className="p-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900/50 rounded-lg">
          <div className="flex items-center gap-3 mb-4">
            <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-lg font-semibold">WCAG 2.2 Level AA Conformance</h3>
          </div>
          <p className="text-sm">
            DIZEE conforms to the <strong className="font-semibold">Web Content Accessibility Guidelines (WCAG) 2.2</strong> at{' '}
            <strong className="font-semibold">Level AA</strong>. These guidelines explain how to make web content more
            accessible for people with disabilities and user-friendly for everyone.
          </p>
        </div>

        <div className="mt-6">
          <p className="text-sm font-semibold mb-3">What this means:</p>
          <List
            items={[
              'Level A: Basic web accessibility features (met)',
              'Level AA: Deals with the biggest and most common barriers for disabled users (met)',
              'Level AAA: Highest level of accessibility (partially met)',
            ]}
          />
        </div>
      </Section>

      {/* Accessibility Features */}
      <Section title="Accessibility Features">
        <Paragraph>We have implemented the following accessibility features:</Paragraph>

        <div className="mt-6 space-y-4">
          <div className="ds-surface p-5">
            <h4 className="text-base font-semibold mb-2">Keyboard Navigation</h4>
            <p className="text-sm">
              All interactive elements can be accessed and operated using only a keyboard (Tab, Shift+Tab, Enter, Space,
              Arrow keys). Focus indicators are clearly visible.
            </p>
          </div>

          <div className="ds-surface p-5">
            <h4 className="text-base font-semibold mb-2">Screen Reader Support</h4>
            <p className="text-sm">
              Compatible with popular screen readers including JAWS, NVDA, VoiceOver, and TalkBack. All images, icons, and
              non-text content have descriptive text alternatives (alt text). Semantic HTML ensures proper content
              structure.
            </p>
          </div>

          <div className="ds-surface p-5">
            <h4 className="text-base font-semibold mb-2">Color Contrast</h4>
            <p className="text-sm">
              All text and interactive elements meet WCAG 2.2 Level AA color contrast ratios (minimum 4.5:1 for normal
              text, 3:1 for large text and UI components). Information is never conveyed by color alone.
            </p>
          </div>

          <div className="ds-surface p-5">
            <h4 className="text-base font-semibold mb-2">Responsive Design</h4>
            <p className="text-sm">
              Fully responsive and mobile-friendly interface that works across devices and screen sizes. Touch targets are
              adequately sized (minimum 44x44 pixels) for easy interaction.
            </p>
          </div>

          <div className="ds-surface p-5">
            <h4 className="text-base font-semibold mb-2">Motion Control</h4>
            <p className="text-sm">
              Respects the "prefers-reduced-motion" browser setting. Users can disable animations through their system
              preferences, ensuring a comfortable experience for those sensitive to motion.
            </p>
          </div>

          <div className="ds-surface p-5">
            <h4 className="text-base font-semibold mb-2">Form Accessibility</h4>
            <p className="text-sm">
              All form fields have clearly associated labels, helpful error messages, and instructions. Required fields are
              clearly marked and validated with descriptive feedback.
            </p>
          </div>
        </div>
      </Section>

      {/* Assistive Technologies */}
      <Section title="Compatible Assistive Technologies">
        <Paragraph>DIZEE has been tested with the following assistive technologies:</Paragraph>
        <List
          items={[
            'Screen readers: JAWS, NVDA (Windows), VoiceOver (macOS/iOS), TalkBack (Android)',
            'Voice control: Dragon NaturallySpeaking, Voice Control (macOS/iOS)',
            'Screen magnifiers: ZoomText, Windows Magnifier, macOS Zoom',
            'Browser extensions: High contrast modes, custom stylesheets',
          ]}
        />
      </Section>

      {/* Technical Specifications */}
      <Section title="Technical Specifications">
        <Paragraph>DIZEE relies on the following technologies for accessibility:</Paragraph>
        <List
          items={[
            'Semantic HTML5 for proper document structure',
            'ARIA (Accessible Rich Internet Applications) attributes for enhanced screen reader support',
            'CSS for visual presentation and responsive design',
            'JavaScript for interactive functionality (with progressive enhancement)',
            'Keyboard event handlers for full keyboard accessibility',
          ]}
        />
      </Section>

      {/* Limitations and Alternatives */}
      <Section title="Known Limitations">
        <Paragraph>
          Despite our best efforts, some areas of DIZEE may not yet be fully accessible. We are aware of the following
          limitations and are actively working to address them:
        </Paragraph>
        <List
          items={[
            'Some third-party integrations may not fully meet WCAG 2.2 Level AA standards',
            'Certain rich media content (videos, audio) may lack complete captions or transcripts',
            'Some complex data visualizations may require alternative text descriptions',
          ]}
        />
        <Paragraph>
          If you encounter any accessibility barriers, please contact us and we will work with you to provide alternative
          access or accommodations.
        </Paragraph>
      </Section>

      {/* Testing Methodology */}
      <Section title="Testing and Evaluation">
        <Paragraph>We regularly test our website using the following methods:</Paragraph>
        <List
          items={[
            'Automated Testing: axe DevTools, Lighthouse, WAVE, eslint-plugin-jsx-a11y',
            'Manual Testing: Keyboard navigation, screen reader testing (NVDA, VoiceOver)',
            'User Testing: Feedback from users with disabilities and accessibility experts',
            'Browser Testing: Chrome, Firefox, Safari, Edge (latest versions)',
            'Device Testing: Desktop, tablet, and mobile devices (iOS and Android)',
          ]}
        />
      </Section>

      {/* Feedback */}
      <Section title="Feedback and Contact">
        <Paragraph>
          We welcome your feedback on the accessibility of DIZEE. Please let us know if you encounter any accessibility
          barriers or have suggestions for improvement:
        </Paragraph>

        <div className="mt-4 ds-surface p-6">
          <p className="text-sm mb-3">
            <strong className="font-semibold">Email:</strong>{' '}
            <a
              href="mailto:contact@dizee.com"
              className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300 transition-colors"
            >
              contact@dizee.com
            </a>
          </p>
          <p className="text-sm mb-4">
            <strong className="font-semibold">Address:</strong> ARTIST 360 INC, Los Angeles, California, USA
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            We aim to respond to accessibility feedback within 2 business days and to propose a solution within 10
            business days.
          </p>
        </div>
      </Section>

      {/* Formal Complaints */}
      <Section title="Formal Complaints">
        <Paragraph>
          If you are not satisfied with our response to your accessibility concern, submit complaints to contact@dizee.com with "Accessibility Complaint" in the subject line. We will acknowledge within 5 business days.
        </Paragraph>
        <Paragraph>
          You may also file a formal complaint with the U.S. Department of Justice, Civil Rights Division. Visit{' '}
          <a
            href="https://www.ada.gov"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300 transition-colors underline"
          >
            www.ada.gov
          </a>{' '}
          for more information.
        </Paragraph>
      </Section>

      {/* Continuous Improvement */}
      <Section title="Continuous Improvement">
        <Paragraph>
          Accessibility is an ongoing effort. We regularly review our platform against current accessibility standards and
          guidelines. Our team receives training on accessibility best practices, and we conduct periodic accessibility
          audits to identify and address issues.
        </Paragraph>
        <Paragraph>
          This statement was created on February 1, 2026, and will be reviewed and updated annually or when significant
          changes are made to the platform.
        </Paragraph>
      </Section>
    </LegalPageLayout>
  )
}
